import { Divider, Stack } from '@mui/material';
import { JSXElementConstructor, useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { ButtonTab } from '@/components/navigation/NavigationTabs';
import { Tabs } from '@/components/navigation/NavigationTabs';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { CombinedSummarySources } from '@/modules/documents/components/CombinedSummarySources';
import { DocumentDetailsViewOptions } from '@/modules/documents/documents.constants';
import { compareCreatedAtDescending } from '@/modules/documents/documents.utils';
import { RegenerateSummaryModal } from '@/modules/documents/RegenerateSummaryModal/RegenerateSummaryModal';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType } from '@/types/schema';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import { useDocumentDetails } from './contexts/DocumentDetails.context';

const truncateSummaryName = (name: string, maxLength: number = 40): string => {
  if (name.length <= maxLength) {
    return name;
  }
  return `${name.slice(0, maxLength)}...`;
};

export interface DocumentSummaryHeaderSlots<
  ContextMenuProps = Record<string, unknown>,
> {
  ContextMenu?: JSXElementConstructor<ContextMenuProps>;
  ContextMenuProps?: ContextMenuProps;
}

interface DocumentSummaryHeaderProps<
  ContextMenuProps = Record<string, unknown>,
> {
  slots?: DocumentSummaryHeaderSlots<ContextMenuProps>;
  documentType: DocumentType | null;
  onOpenChatWithSummary?: () => void;
}

interface DocumentSummaryTabsProps {
  activeTab: DocumentDetailsViewOptions;
  setActiveTab: (value: DocumentDetailsViewOptions) => void;
}

export function DocumentSummaryTabs({
  activeTab,
  setActiveTab,
}: DocumentSummaryTabsProps) {
  return (
    <Tabs>
      <ButtonTab
        display="Summary"
        isActive={activeTab === 'summary'}
        onClick={() => setActiveTab('summary')}
      />
      <ButtonTab
        display="Document"
        isActive={activeTab === 'document'}
        onClick={() => setActiveTab('document')}
      />
    </Tabs>
  );
}

export function DocumentSummaryHeader<
  ContextMenuProps = Record<string, unknown>,
>({
  slots,
  documentType,
  onOpenChatWithSummary,
}: DocumentSummaryHeaderProps<ContextMenuProps>) {
  const {
    setIsGeneratingAiSummaryOverride,
    documentId,
    documentSummary,
    openEditSummaryTrowser,
    summaries,
    setSelectedSummaryId,
    setTransitionToNewSummaryWhenAvailable,
  } = useDocumentDetails();
  const canEdit = useCanEditHouseholdData();
  const [modalState, { openModal, closeModal }] = useModalState<boolean>();

  const handleEditSummary = useCallback(() => {
    openEditSummaryTrowser();
  }, [openEditSummaryTrowser]);

  const handleRegenerateSummary = useCallback(
    (fromAddNew: boolean) => {
      return () => {
        openModal(fromAddNew);
      };
    },
    [openModal]
  );

  const handleStartRegenerateSummarySuccess = useCallback(() => {
    setIsGeneratingAiSummaryOverride(true);
  }, [setIsGeneratingAiSummaryOverride]);

  if (!documentSummary) {
    return null;
  }

  const selectedSummarySourceDocuments = getNodes(
    documentSummary.sourceDocuments
  );

  return (
    <>
      {modalState.isModalOpen && documentType && (
        <RegenerateSummaryModal
          isOpen={modalState.isModalOpen}
          onClose={closeModal}
          documentId={documentId}
          documentType={documentType}
          currentSummaryId={documentSummary.id}
          currentSummaryName={documentSummary.displayName}
          currentSummaryPromptId={documentSummary.generatedFromPrompt?.id}
          openedFromAddNew={modalState.data ?? false}
          onSuccess={handleStartRegenerateSummarySuccess}
          setTransitionToNewSummaryWhenAvailable={
            setTransitionToNewSummaryWhenAvailable
          }
        />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          {summaries && summaries.length > 0 && (
            <ButtonWithPopover
              size="sm"
              variant="secondary"
              buttonSx={{ height: 36, py: 0.5 }}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>
                    {truncateSummaryName(
                      documentSummary.displayName?.trim() || 'Select summary',
                      40
                    )}
                  </span>
                  {selectedSummarySourceDocuments.length > 1 && (
                    <CombinedSummarySources
                      sourceDocuments={selectedSummarySourceDocuments}
                    />
                  )}
                </Stack>
              }
              popperVariant="menuBelow"
            >
              {canEdit && (
                <MenuItem
                  icon={<PlusIcon size={16} />}
                  label="Create new"
                  onClick={handleRegenerateSummary(true)}
                />
              )}
              <Divider />
              {[...summaries]
                .sort(compareCreatedAtDescending)
                .map((summary) => {
                  const summaryName = summary.displayName?.trim() || 'Summary';
                  const sourceDocuments = getNodes(summary.sourceDocuments);
                  const isCombined = sourceDocuments.length > 1;
                  const generatedDate = formatDateToMMDDYY(
                    new Date(summary.createdAt)
                  );
                  const summaryDescription = isCombined
                    ? `Combined, generated ${generatedDate}. Sources: ${sourceDocuments
                        .map((document) => document.name)
                        .join(', ')}`
                    : `Generated ${generatedDate}`;

                  return (
                    <MenuItem
                      key={summary.id}
                      label={summaryName}
                      subtext={`Generated ${generatedDate}`}
                      iconAfter={
                        isCombined ? (
                          <CombinedSummarySources
                            sourceDocuments={sourceDocuments}
                            showDetails
                          />
                        ) : undefined
                      }
                      muiMenuItemProps={{
                        'aria-label': `${summaryName}. ${summaryDescription}`,
                      }}
                      onClick={() => setSelectedSummaryId(summary.id)}
                    />
                  );
                })}
            </ButtonWithPopover>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {canEdit && (
            <Button
              startIcon={Stars01Icon}
              variant="secondary"
              size="sm"
              onClick={handleRegenerateSummary(false)}
              iconProps={{ color: COLORS.TEAL['600'], size: 16 }}
            >
              Regenerate
            </Button>
          )}
          {canEdit && onOpenChatWithSummary && (
            <Button
              startIcon={Stars01Icon}
              variant="secondary"
              size="sm"
              onClick={onOpenChatWithSummary}
              iconProps={{ color: COLORS.TEAL['600'], size: 16 }}
            >
              Chat
            </Button>
          )}
          {canEdit && (
            <IconButton
              icon={Edit02Icon}
              ariaLabel="Edit summary"
              variant="secondary"
              size="sm"
              onClick={handleEditSummary}
            />
          )}
          {slots?.ContextMenu && slots.ContextMenuProps && (
            <slots.ContextMenu {...slots.ContextMenuProps} />
          )}
        </Stack>
      </Stack>
    </>
  );
}
